/**
 * Today: fixes on their way (core whatidid.ts openFixViews). "Waiting on the repair: walk-in" with what to do meanwhile and its
 * source, and the one action: "It's fixed" (then the recheck reading), "It came" for an order (the order list is one tap away),
 * "Take the recheck", or, for the person in charge, "Pick what was done" on a hand-off.
 *
 * The moment: the next person on shift opens Today and needs to know the walk-in is broken before they load it. The meanwhile
 * line is the instruction (keep TCS food in the reach-in), so it is shown, not folded. Everyone sees it: staff act on it.
 */
import { useNavigate } from "react-router-dom";
import { recheckKey, type OpenFixView } from "@logwave/core";
import { Icon } from "./Icons.tsx";
import { Button, Card, LinkButton, Pill, useToast } from "./ui.tsx";
import { useLoad } from "../lib/useLoad.ts";
import { isoToLocalTime } from "../lib/time.ts";
import { useApi, useSession } from "../session.tsx";

const KICKER: Record<OpenFixView["status"], string> = { waiting: "Fix on its way", handed: "Handed off", recheck: "Recheck" };

export function WaitingFixes({ isPic }: { isPic: boolean }) {
  const api = useApi();
  const s = useSession();
  const nav = useNavigate();
  const toast = useToast();
  // Older servers: no route, no card.
  const data = useLoad(() => api.openFixes(s.org.id, s.location.id).then((r) => r.fixes).catch(() => [] as OpenFixView[]), [api, s.org.id, s.location.id]);
  const fixes = data.data ?? [];
  if (!fixes.length) return null;
  const recheck = (f: OpenFixView) => nav(`/do?check=${encodeURIComponent(f.recheckKey ?? recheckKey(f.entryId))}`);
  async function arrived(f: OpenFixView) {
    try {
      await api.fixArrived(s.org.id, f.entryId, { version: f.version ?? 1 });
      toast.show(`Thanks ✓ ${f.what}: take the recheck reading now.`, { icon: "check" });
      recheck(f);
    } catch (x) { toast.show(x instanceof Error ? x.message : String(x), { icon: "alert" }); void data.reload(); }
  }
  return (
    <section className="fixes-waiting" aria-label="Fixes on their way">
      {fixes.map((f) => (
        <Card key={f.entryId} edge="warn" className="stack-sm">
          <Pill tone="warn" icon={f.status === "handed" ? "handoff" : f.status === "recheck" ? "thermo" : f.action.kind === "arrived" && f.supplyKey ? "cart" : "wrench"}>{KICKER[f.status]}</Pill>
          <h2 className="fcard-title">{f.title}</h2>
          {f.interim && f.status === "waiting" && <p className="wid-hold"><Icon name="clock" size={16} /><span>Meanwhile: {f.interim.text} <span className="wid-src">{f.interim.source}</span></span></p>}
          {f.interim && f.status === "handed" && <p className="caption">{f.interim.text}</p>}
          {f.status === "recheck" && f.recheckAt && <p className="caption">Due {isoToLocalTime(f.recheckAt, s.location.timezone)}. Check it again and save the reading.</p>}
          <div className="btn-row">
            {f.status === "waiting" && <Button big icon="check" onClick={() => void arrived(f)}>{f.action.label}</Button>}
            {f.status === "waiting" && f.supplyKey && !f.arrived && <LinkButton variant="secondary" icon="cart" to="/order">Open the order list</LinkButton>}
            {f.status === "recheck" && <Button big icon="thermo" onClick={() => recheck(f)}>{f.action.label}</Button>}
            {f.status === "handed" && isPic && <LinkButton big icon="write" to={`/do/fix/${encodeURIComponent(f.entryId)}`}>{f.action.label}</LinkButton>}
          </div>
        </Card>
      ))}
    </section>
  );
}
